import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Modal,
  Row,
  Space,
  Typography,
  Input,
  Table,
  Divider,
  Spin,
  Tooltip,
} from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { LS_DEFAULT_WAREHOUSE_KEY } from 'constants/config';
import { getWarehouseFromCache } from 'utils';
import { fetchWarehouseLocationList } from 'services/warehouse';
import { adjustPack } from 'services/prepack';

const { Text } = Typography;
type DataItem = {
  id: string;
  prepackSKU: string;
  packQty: number;
  fromLocationCode: string;
  fromLocationNum?: number;
  toLocationCode: string;
  toLocationNum?: number;
  status?: 'success' | 'error';
  message?: string;
};
type Props = {
  // warehouseNum: number;
  onHide: () => void;
  onFinish: () => void;
};

// eslint-disable-next-line
export default (props: Props) => {
  const { onHide, onFinish } = props;
  const [data, setData] = useState<DataItem[]>([]);
  const [validData, setValidData] = useState<DataItem[]>([]);
  const [str, setStr] = useState<string>('');
  const [inited, setInited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentFromWarehouse, setCurrentFromWarehouse] =
    useState<StringKAnyVPair>({});
  const [sourceLocations, setSourceLocations] = useState<any[]>([]);
  const [step, setStep] = useState<'start' | 'preview' | 'finish'>('start');

  const checkStr = useCallback(
    async (str: string) => {
      const temp = str.split('\n');

      const tempArr: DataItem[] = [];
      temp.map((i, index) => {
        const [str1, str2, str3, str4] = i.split('\t');
        if (str1 && str2 && str3 && str4) {
          const loc3 = sourceLocations.filter((j) => j.locationCode === str3);
          const loc4 = sourceLocations.filter((j) => j.locationCode === str4);
          const isInvalidNumber =
            Number(str2) > 0 && Number.isInteger(Number(str2));
          tempArr.push({
            id: `row${index}`,
            prepackSKU: str1,
            packQty: isInvalidNumber ? Number(str2) : NaN,
            fromLocationCode: str3,
            fromLocationNum: loc3.length > 0 ? loc3[0].locationNum : undefined,
            toLocationCode: str4,
            toLocationNum: loc4.length > 0 ? loc4[0].locationNum : undefined,
          });
          // }
        }
        return true;
      });
      setData(tempArr);
    },
    [sourceLocations]
  );

  const loadInitialData = useCallback(async () => {
    setIsLoading(true);

    try {
      const whNum = parseInt(
        localStorage.getItem(LS_DEFAULT_WAREHOUSE_KEY) || '-1'
      );
      //const wh = props.warehouse.warehouseId || 0;
      const wh = getWarehouseFromCache(whNum);

      if (!wh) {
        setIsLoading(false);
        return;
      }
      setCurrentFromWarehouse(wh);

      const locations = await fetchWarehouseLocationList(wh.warehouseId, true);

      if (Array.isArray(locations)) {
        setSourceLocations(locations);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * handle submit event
   */
  const handleSubmit = useCallback(async () => {
    const newData = [...validData];
    setIsLoading(true);

    const params = newData.map((item) => {
      return {
        WarehouseNum: currentFromWarehouse.warehouseNum,
        PrepackSKU: item.prepackSKU,
        ToLocationNum: item.toLocationNum,
        FromLocationNum: item.fromLocationNum,
        PackQty: item.packQty,
      };
    });
    let updatedData: DataItem[] = [];
    try {
      const res = await adjustPack(params);
      if (res.isSuccess) {
        updatedData = newData.map((item, index) => {
          const responseItem = res.data[index];
          if (responseItem) {
            return {
              ...item,
              status: responseItem.Success ? 'success' : 'error',
              message: responseItem.Message || undefined,
            };
          } else {
            return {
              ...item,
              status: 'error',
              message: 'no response',
            };
          }
        });
      } else {
        updatedData = newData.map((item) => ({
          ...item,
          status: 'error' as const,
          message: res.message || 'Request failed',
        }));
      }
    } catch (error) {
      updatedData = newData.map((item) => ({
        ...item,
        status: 'error' as const,
        message: 'Network error occurred',
      }));
    }
    setValidData(updatedData);
    setStep('finish');
    setIsLoading(false);
  }, [validData, currentFromWarehouse.warehouseNum]);
  /**
   * handle cancel event
   */
  const handleCancel = useCallback(async () => {
    if (step === 'finish') {
      onFinish();
    } else {
      onHide();
    }
  }, [onHide, onFinish, step]);

  const columns: ColumnsType<any> = [
    {
      title: 'PrepackSKU',
      dataIndex: 'prepackSKU',
      key: 'prepackSKU',
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: 'Pack Qty',
      dataIndex: 'packQty',
      key: 'qty',
      render: (text, record) =>
        record.packQty > 0 ? (
          <Text>{text}</Text>
        ) : (
          <Text style={{ color: '#ff4d4f' }} delete>
            {text}
          </Text>
        ),
    },
    {
      title: 'From Location',
      dataIndex: 'fromLocationCode',
      key: 'fromLoacation',
      render: (text, record) =>
        record.fromLocationNum ? (
          <Text>{text}</Text>
        ) : (
          <Text style={{ color: '#ff4d4f' }} delete>
            {text}
          </Text>
        ),
    },
    {
      title: 'To Location',
      dataIndex: 'toLocationCode',
      key: 'toLoacation',
      render: (text, record) =>
        record.toLocationNum ? (
          <Text>{text}</Text>
        ) : (
          <Text style={{ color: '#ff4d4f' }} delete>
            {text}
          </Text>
        ),
    },
  ];
  const columns2: ColumnsType<any> = [
    {
      title: 'PrepackSKU',
      dataIndex: 'prepackSKU',
      key: 'prepackSKU',
    },
    {
      title: 'Pack Qty',
      dataIndex: 'packQty',
      key: 'qty',
    },
    {
      title: 'From Location',
      dataIndex: 'fromLocationCode',
      key: 'fromLoacation',
    },
    {
      title: 'To Location',
      dataIndex: 'toLocationCode',
      key: 'toLoacation',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Space>
          <Tooltip placement="topRight" title={record.message}>
            <Text
              style={{
                color:
                  status === 'success'
                    ? 'green'
                    : status === 'error'
                    ? 'red'
                    : '',
              }}
            >
              {status || 'pending'}
            </Text>
          </Tooltip>
          {!!record.message && <Text copyable={{ text: record.message }} />}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (!inited) {
      loadInitialData();
      setInited(true);
    }
  }, [inited, loadInitialData]);

  useEffect(() => {
    if (step === 'start') {
      setData([]);
      setValidData([]);
    }
  }, [step]);

  return (
    <Modal
      title="Bulk Pack Adjustment"
      centered
      visible={true}
      width={800}
      destroyOnClose={true}
      maskClosable={false}
      onCancel={handleCancel}
      footer={
        <Row justify="space-between">
          <Space>
            {step === 'preview' && (
              <Button
                key="submit"
                disabled={data.length === 0 || isLoading}
                onClick={() => {
                  setStep('start');
                }}
              >
                Back
              </Button>
            )}
          </Space>
          <Space>
            {step === 'start' && (
              <Button
                key="submit"
                type="primary"
                disabled={data.length === 0}
                onClick={() => {
                  setValidData(
                    data.filter(
                      (i) =>
                        i.fromLocationNum && i.toLocationNum && i.packQty > 0
                    )
                  );
                  setStep('preview');
                }}
                icon={<CheckOutlined />}
              >
                Continue
              </Button>
            )}

            {step === 'preview' && (
              <Button
                key="submit"
                type="primary"
                disabled={data.length === 0}
                loading={isLoading}
                onClick={handleSubmit}
                icon={<CheckOutlined />}
              >
                Adjustment
              </Button>
            )}
            <Button onClick={handleCancel} icon={<CloseOutlined />}>
              {step === 'finish' ? 'Close' : 'Cancel'}
            </Button>
          </Space>
        </Row>
      }
    >
      <Spin spinning={isLoading}>
        {step === 'start' && (
          <>
            <Row style={{ marginBottom: 8 }}>
              <Row>
                <Space split={<Divider type="vertical" />}>
                  <Typography.Text>PrepackSKU</Typography.Text>
                  <Typography.Text>Pack Qty</Typography.Text>
                  <Typography.Text>From Location</Typography.Text>
                  <Typography.Text>To Location</Typography.Text>
                </Space>
              </Row>

              <Space align="center">
                <Input.TextArea
                  style={{ width: 550 }}
                  placeholder="Please paste the excel cells content"
                  rows={16}
                  value={str}
                  onChange={(e) => setStr(e.target.value)}
                />
                <Button
                  type="primary"
                  disabled={str.length === 0}
                  onClick={() => {
                    checkStr(str);
                  }}
                >
                  Process
                </Button>
              </Space>
            </Row>
            <Table
              rowKey="id"
              scroll={{ y: 322 }}
              dataSource={data}
              columns={columns}
              pagination={false}
            />
          </>
        )}
        {step !== 'start' && (
          <Table
            rowKey="id"
            scroll={{ y: 322 }}
            dataSource={validData}
            columns={columns2}
            pagination={false}
          />
        )}
      </Spin>
    </Modal>
  );
};
